import performRequest from '../../axios';

/**
 * Admin console API.
 *
 * Mirrors routes/admin.ts in blastoise. Every endpoint here is guarded
 * server-side by allowOnlySuperAdminRole — the UI hiding a button is a
 * convenience, not the access control.
 */
export const adminAPI = {
  /**
   * Whether the signed-in user is on the platform_admins allowlist.
   * Returns { isPlatformAdmin } rather than 403ing, so a normal user's app
   * can check without logging an error.
   */
  whoami: async() => performRequest('api/admin/whoami', 'GET'),

  // ---- lead inbox --------------------------------------------------------

  /** kind: 'support' | 'sales' */
  getLeads: async(kind, { status, search, pageNo = 1, pageSize = 25 } = {}) => {
    let query = `pageNo=${pageNo}&pageSize=${pageSize}`;
    if (status && status !== 'ALL') query += `&status=${encodeURIComponent(status)}`;
    if (search) query += `&search=${encodeURIComponent(search)}`;
    return performRequest(`api/admin/leads/${kind}?${query}`, 'GET');
  },

  getLeadCounts: async(kind) => performRequest(`api/admin/leads/${kind}/counts`, 'GET'),

  updateLead: async(kind, id, updates) =>
    performRequest(`api/admin/leads/${kind}/${id}`, 'PATCH', updates),

  // ---- credits -----------------------------------------------------------

  getCreditOverview: async({ pageNo = 1, pageSize = 25, search } = {}) => {
    let query = `pageNo=${pageNo}&pageSize=${pageSize}`;
    if (search) query += `&search=${encodeURIComponent(search)}`;
    return performRequest(`api/admin/credits/overview?${query}`, 'GET');
  },

  getUsage: async({ days = 30, organizationId } = {}) => {
    let query = `days=${days}`;
    if (organizationId) query += `&organizationId=${encodeURIComponent(organizationId)}`;
    return performRequest(`api/admin/credits/usage?${query}`, 'GET');
  },

  /** Per-user spend inside one org (platform admin view). */
  getUsageByUser: async(organizationId, { days = 30 } = {}) =>
    performRequest(`api/admin/credits/by-user/${organizationId}?days=${days}`, 'GET'),

  getOrgLedger: async(organizationId, { pageNo = 1, pageSize = 50 } = {}) =>
    performRequest(
      `api/admin/credits/ledger/${organizationId}?pageNo=${pageNo}&pageSize=${pageSize}`,
      'GET'
    ),

  /** The demo-grant action. `reason` is required by the server. */
  grantCredits: async({ organizationId, credits, reason }) =>
    performRequest('api/admin/credits/grant', 'POST', { organizationId, credits, reason }),

  adjustCredits: async({ organizationId, delta, reason }) =>
    performRequest('api/admin/credits/adjust', 'POST', { organizationId, delta, reason }),

  // ---- pricing configuration --------------------------------------------

  getPacks: async() => performRequest('api/admin/pricing/packs', 'GET'),
  updatePack: async(id, updates) =>
    performRequest(`api/admin/pricing/packs/${id}`, 'PATCH', updates),

  getRates: async() => performRequest('api/admin/pricing/rates', 'GET'),
  updateRate: async(id, updates) =>
    performRequest(`api/admin/pricing/rates/${id}`, 'PATCH', updates)
};

export default adminAPI;
